import React, { useState, useEffect, useCallback } from 'react';
import leaveService from '../api/leaveService';
import authService from '../api/authService';
import trainerService from '../api/trainerService';
import './LeaveManagement.css';

const LeaveManagement = () => {
  // Auth
  const currentUser = authService.getCurrentUser();
  const isScheduler = currentUser && currentUser.role === 'scheduler';

  // State
  const [myLeaves, setMyLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isScheduler ? 'team' : 'my-leaves');

  // Trainers list (for scheduler to add leave on behalf of facilitators)
  const [trainers, setTrainers] = useState([]);

  // Form state
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyForm, setApplyForm] = useState({
    trainerId: '',
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: 'casual'
  });
  const [submitting, setSubmitting] = useState(false);

  // Fetch data
  const fetchMyLeaves = useCallback(async () => {
    try {
      const response = await leaveService.getMyLeaves();
      if (response.data.success) {
        setMyLeaves(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching my leaves:', error);
    }
  }, []);

  const fetchAllLeaves = useCallback(async () => {
    try {
      const response = await leaveService.getAllLeaves();
      if (response.data.success) {
        setAllLeaves(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching all leaves:', error);
    }
  }, []);

  const fetchLeaveStats = useCallback(async () => {
    try {
      const response = await leaveService.getLeaveStats();
      if (response.data.success) {
        setLeaveStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching leave stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await fetchMyLeaves();
        if (isScheduler) {
          await Promise.all([fetchAllLeaves(), fetchLeaveStats()]);
          try {
            const trainersRes = await trainerService.getAllTrainers();
            setTrainers(trainersRes.data || []);
          } catch (err) {
            console.error('Error fetching trainers:', err);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchMyLeaves, fetchAllLeaves, fetchLeaveStats, isScheduler]);

  // Record a leave
  const handleApplySubmit = async (e) => {
    e.preventDefault();

    if (applyForm.start_date && applyForm.end_date) {
      const { workingDays } = leaveService.calculateWorkingDays(applyForm.start_date, applyForm.end_date);
      if (workingDays === 0) {
        alert('All selected dates are non-working days. Please pick dates that include at least one working day.');
        return;
      }
    }

    setSubmitting(true);

    try {
      const submitData = { ...applyForm };
      if (submitData.trainerId) {
        submitData.trainerId = parseInt(submitData.trainerId);
      } else {
        delete submitData.trainerId;
      }
      const response = await leaveService.applyLeave(submitData);
      if (response.data.success) {
        let message = 'Leave recorded successfully.';
        if (response.data.leaveDetails) {
          const { workingDays, offDays } = response.data.leaveDetails;
          if (offDays > 0) {
            message += `\n\nNote: ${workingDays} working day(s) in range. ${offDays} day(s) are off days and won't count as leave.`;
          }
        }
        alert(message);
        setShowApplyModal(false);
        setApplyForm({ trainerId: '', start_date: '', end_date: '', reason: '', leave_type: 'casual' });
        fetchMyLeaves();
        if (isScheduler) {
          fetchAllLeaves();
          fetchLeaveStats();
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error recording leave');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete a recorded leave
  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm('Delete this leave record? This cannot be undone.')) return;

    try {
      const response = await leaveService.deleteLeave(leaveId);
      if (response.data.success) {
        alert('Leave deleted');
        fetchMyLeaves();
        if (isScheduler) {
          fetchAllLeaves();
          fetchLeaveStats();
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting leave');
    }
  };

  // Owner can delete only future leaves; scheduler in the team view can delete any.
  const canDeleteRow = (leave, viewingTab) => {
    if (viewingTab === 'team' && isScheduler) return true;
    if (viewingTab === 'my-leaves') {
      return leaveService.computeStatus(leave) !== 'past';
    }
    return false;
  };

  // Render leave table
  const renderLeaveTable = (leaves, viewingTab, showTrainer = false) => {
    if (leaves.length === 0) {
      return <p className="no-data">No leaves recorded</p>;
    }

    return (
      <table className="leave-table">
        <thead>
          <tr>
            {showTrainer && <th>Facilitator</th>}
            <th>Leave Type</th>
            <th>From</th>
            <th>To</th>
            <th>Days</th>
            <th>Reason</th>
            <th>Status</th>
            <th>Recorded On</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {leaves.map(leave => {
            const computed = leaveService.computeStatus(leave);
            const showDelete = canDeleteRow(leave, viewingTab);
            return (
              <tr key={leave.id} className={`leave-row ${computed}`}>
                {showTrainer && <td className="trainer-cell">{leave.trainer?.name || '-'}</td>}
                <td>
                  <span className={`leave-type-badge ${leave.leave_type}`}>
                    {leaveService.getLeaveTypeLabel(leave.leave_type)}
                  </span>
                </td>
                <td>{leaveService.formatDate(leave.start_date)}</td>
                <td>{leaveService.formatDate(leave.end_date)}</td>
                <td className="days-cell">
                  {leaveService.calculateDays(leave.start_date, leave.end_date)}
                </td>
                <td className="reason-cell">{leave.reason}</td>
                <td>
                  <span className={`status-badge ${leaveService.getStatusClass(computed)}`}>
                    {computed.charAt(0).toUpperCase() + computed.slice(1)}
                  </span>
                </td>
                <td>{leaveService.formatDateTime(leave.requested_at)}</td>
                <td className="actions-cell">
                  {showDelete ? (
                    <button
                      className="btn-cancel"
                      onClick={() => handleDeleteLeave(leave.id)}
                      title="Delete this leave record"
                    >
                      Delete
                    </button>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div className="leave-management-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading leave data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="leave-management-page">
      <div className="page-header">
        <h1>Leave Information</h1>
        <p className="page-subtitle">
          {isScheduler
            ? 'View team leaves; add on behalf of a facilitator if needed.'
            : 'Record your leave dates here so they show as non-availability during scheduling.'}
        </p>
      </div>

      {/* Stats for Scheduler */}
      {isScheduler && leaveStats && (
        <div className="leave-stats">
          <div className="stat-card pending">
            <span className="stat-value">{leaveStats.upcoming}</span>
            <span className="stat-label">Upcoming</span>
          </div>
          <div className="stat-card approved">
            <span className="stat-value">{leaveStats.ongoing}</span>
            <span className="stat-label">Ongoing</span>
          </div>
          <div className="stat-card rejected">
            <span className="stat-value">{leaveStats.past}</span>
            <span className="stat-label">Past</span>
          </div>
          <div className="stat-card total">
            <span className="stat-value">{leaveStats.total}</span>
            <span className="stat-label">Total ({leaveStats.year})</span>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="action-bar">
        <div className="tabs">
          {isScheduler && (
            <button
              className={`tab ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              Team Leaves ({allLeaves.length})
            </button>
          )}
          <button
            className={`tab ${activeTab === 'my-leaves' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-leaves')}
          >
            My Leaves
          </button>
        </div>

        <button className="btn-apply" onClick={() => setShowApplyModal(true)}>
          + Add Leave
        </button>
      </div>

      {/* Leave Tables */}
      <div className="leave-content">
        {activeTab === 'team' && isScheduler && (
          <div className="leave-section">
            <h2>Team Leaves</h2>
            {renderLeaveTable(allLeaves, 'team', true)}
          </div>
        )}

        {activeTab === 'my-leaves' && (
          <div className="leave-section">
            <h2>My Leaves</h2>
            {renderLeaveTable(myLeaves, 'my-leaves', false)}
          </div>
        )}
      </div>

      {/* Add Leave Modal */}
      {showApplyModal && (
        <div className="modal-backdrop">
          <div className="modal-content apply-leave-modal">
            <button className="modal-close" onClick={() => setShowApplyModal(false)}>
              &times;
            </button>
            <h2>Add Leave</h2>

            <form onSubmit={handleApplySubmit}>
              {isScheduler && trainers.length > 0 && (
                <div className="form-group">
                  <label>Record for Facilitator</label>
                  <select
                    value={applyForm.trainerId}
                    onChange={(e) => setApplyForm({ ...applyForm, trainerId: e.target.value })}
                  >
                    <option value="">-- Myself --</option>
                    {trainers
                      .filter(t => t.role !== 'scheduler')
                      .map(trainer => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Leave Type</label>
                <select
                  value={applyForm.leave_type}
                  onChange={(e) => setApplyForm({ ...applyForm, leave_type: e.target.value })}
                  required
                >
                  <option value="casual">Casual Leave</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Leave</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={applyForm.start_date}
                    onChange={(e) => setApplyForm({ ...applyForm, start_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={applyForm.end_date}
                    onChange={(e) => setApplyForm({ ...applyForm, end_date: e.target.value })}
                    min={applyForm.start_date || new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>

              {applyForm.start_date && applyForm.end_date && (() => {
                const totalDays = leaveService.calculateDays(applyForm.start_date, applyForm.end_date);
                const { workingDays, offDays } = leaveService.calculateWorkingDays(applyForm.start_date, applyForm.end_date);
                return (
                  <div className="days-preview-detailed">
                    <div className="days-preview">
                      Working Days: <strong>{workingDays}</strong> of {totalDays} total days
                    </div>
                    {offDays.length > 0 && (
                      <div className="off-days-info">
                        <small>
                          Off days excluded: {offDays.map(d => `${d.reason}`).join(', ')}
                        </small>
                      </div>
                    )}
                    {workingDays === 0 && (
                      <div className="warning-message">
                        All selected dates are off days. Please pick different dates.
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="form-group">
                <label>Reason / Note</label>
                <textarea
                  value={applyForm.reason}
                  onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })}
                  placeholder="Short description of the leave (visible to the scheduler)."
                  rows={4}
                  required
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowApplyModal(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="btn-submit">
                  {submitting ? 'Saving...' : 'Save Leave'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
